import type { AppContext } from '@application/context/AppContext';
import { roleToShellPerm } from '@application/access/ViewAccessPolicy';
import { runBackupExport } from '@application/backup/BackupActions';
import {
  UpdateService,
  type UpdateManifest,
  type UpdateProgress,
} from '@infra/update/UpdateService';
import { UpdateStateMachine } from '@infra/update/UpdateStateMachine';
import { UsbUpdateService } from '@infra/update/UsbUpdateService';
import type { LicensingLatestRelease } from '@infra/update/NexxusUpdateManifest';
import { detectReleasePlatform, latestReleaseUrl } from '@infra/update/licensingApiUrl';
import { ToastService } from '@presentation/components/Toast';
import { logger } from '@infra/telemetry/Logger';
import { confirmAction } from '@presentation/components/ConfirmDialog';
import {
  updateMandatoryBackupMessage,
  updatePreserveConfirmMessage,
} from '@domain/update/UpdateDataPreservation';
import type { Session } from '@shared/types/User';
import { compareAppVersions, isSha256Hex } from '@shared/utils/compareAppVersions';

const PRE_UPDATE_BACKUP_KEY = 'nexxus_pre_update_backup_ok';

function isAdminRole(role: Session['role'] | undefined): boolean {
  return Boolean(role && roleToShellPerm(role) === 'admin');
}

/** Orchestration mise à jour avec sauvegarde obligatoire et machine d'état. */
export class UpdateCoordinator {
  private readonly updates: UpdateService;
  private readonly safety = new UpdateStateMachine();
  private readonly usb = new UsbUpdateService();

  constructor(
    private readonly ctx: AppContext,
    private readonly currentVersion: string,
    /** Session shell en mémoire (évite faux refus si stockage désynchronisé). */
    private readonly sessionHint?: () => Session | null,
  ) {
    this.updates = new UpdateService(this.currentVersion, this.ctx.db);
  }

  async checkOnline(): Promise<UpdateManifest | null> {
    const state = await window.electron?.getUpdateState?.();
    if (state?.status === 'recovery_required') {
      ToastService.error(
        'La mise à jour précédente n’a pas été activée. Restaurez la version et la sauvegarde précédentes avant de réessayer.',
      );
      return null;
    }
    return this.updates.checkForUpdate();
  }

  /** Vérifie puis installe la dernière release (un clic depuis Paramètres / bandeau). */
  async upgradeToLatest(): Promise<boolean> {
    const manifest = await this.checkOnline();
    if (!manifest) {
      ToastService.error(
        'Impossible de vérifier les mises à jour — réseau ou serveur indisponible',
      );
      return false;
    }
    if (!manifest.is_outdated) {
      ToastService.success(`Application à jour (v${this.currentVersion})`);
      return false;
    }
    return this.applyOnline(manifest);
  }

  onProgress(callback: (progress: UpdateProgress) => void): void {
    this.updates.onProgress(callback);
  }

  async fetchLatestRelease(): Promise<LicensingLatestRelease | null> {
    try {
      const res = await fetch(latestReleaseUrl(), { signal: AbortSignal.timeout(10_000) });
      if (!res.ok) return null;
      return (await res.json()) as LicensingLatestRelease;
    } catch {
      return null;
    }
  }

  /**
   * Droits admin pour MAJ : stockage auth, session shell, ou badge UI Administrateur.
   * Évite le faux message « session admin requise » alors que le badge affiche Administrateur.
   */
  private resolveAdminSession(): Session | null {
    let session = this.ctx.auth.getSession();
    if (!isAdminRole(session?.role)) {
      const hint = this.sessionHint?.() ?? null;
      if (hint && isAdminRole(hint.role)) {
        this.ctx.auth.restoreSession(hint);
        session = this.ctx.auth.getSession() ?? hint;
        logger.warn({ event: 'update_admin_session_restored_from_shell' });
      }
    }
    if (session && isAdminRole(session.role)) {
      this.ctx.auth.touchSession();
      return this.ctx.auth.getSession() ?? session;
    }
    return null;
  }

  /** Aligné sur le badge ADMINISTRATEUR (permission shell), pas seulement sessionStorage. */
  private hasAdminRights(): boolean {
    if (this.resolveAdminSession()) return true;
    if (isAdminRole(this.sessionHint?.()?.role)) return true;
    if (typeof document !== 'undefined' && document.querySelector('.app.role-admin')) {
      logger.warn({ event: 'update_admin_shell_ui_fallback' });
      return true;
    }
    return false;
  }

  private refuseNonAdmin(): false {
    if (!this.ctx.auth.getSession() && !isAdminRole(this.sessionHint?.()?.role)) {
      ToastService.error('Session expirée — reconnectez-vous pour installer la mise à jour');
    } else {
      ToastService.error('Droits administrateur requis pour appliquer une mise à jour');
    }
    return false;
  }

  private assertAdminSession(): boolean {
    if (this.hasAdminRights()) return true;
    return this.refuseNonAdmin();
  }

  /** Refuse toute version non strictement supérieure (rétrogradation / réinstallation). */
  private assertStrictlyNewer(version: string): boolean {
    if (compareAppVersions(version, this.currentVersion) > 0) return true;
    ToastService.error(
      `Version ${version} non strictement supérieure à la version installée (${this.currentVersion}) — mise à jour refusée`,
    );
    return false;
  }

  private assertPlatformMatch(platform: string): boolean {
    const local = detectReleasePlatform();
    if (platform !== local) {
      ToastService.error(
        `Cette release cible ${platform} — ce poste est ${local}. Téléchargez la bonne plateforme.`,
      );
      return false;
    }
    return true;
  }

  private async assertSafeToApply(manifest: UpdateManifest): Promise<boolean> {
    const adminOk = this.hasAdminRights();
    if (manifest.install_mode === 'browser') {
      if (!adminOk) return this.refuseNonAdmin();
      return true;
    }
    const session = this.resolveAdminSession() ?? this.ctx.auth.getSession();
    const tenantId = session?.tenant_id ?? this.ctx.tenantContext.tryGetTenant()?.id;
    const disk = await window.electron?.getUpdateDiskSpace?.();
    const requiredBytes = Math.max((manifest.size_bytes ?? 512 * 1024 * 1024) * 2, 1024 ** 3);
    const evaluation = await this.safety.evaluate(manifest, {
      currentVersion: this.currentVersion,
      tenantId,
      hasActiveSession: adminOk,
      diskSpaceOk: disk ? disk.freeBytes >= requiredBytes : undefined,
    });
    if (!evaluation.allowed) {
      ToastService.error(`Mise à jour refusée : ${evaluation.reasons.join(' ; ')}`);
      return false;
    }
    return true;
  }

  /** Empreinte SHA-256 avant toute sauvegarde / installation (sauf ouverture navigateur). */
  private assertChecksumBeforeBackup(manifest: UpdateManifest): boolean {
    if (manifest.install_mode === 'browser') return true;
    if (isSha256Hex(manifest.checksum_sha256)) return true;
    ToastService.error(
      'Empreinte SHA-256 obligatoire avant toute installation. Téléchargez l’installateur depuis le site Nexxus.',
    );
    return false;
  }

  /** Sauvegarde chiffrée obligatoire avant toute application. */
  private async mandatoryBackup(targetVersion: string): Promise<boolean> {
    this.updates.report({
      stage: 'backing_up',
      percent: 8,
      message: 'Sauvegarde chiffrée obligatoire avant installation…',
    });
    const ok = await runBackupExport(this.ctx, undefined, {
      title: 'Sauvegarde avant mise à jour (données préservées)',
      message: updateMandatoryBackupMessage(targetVersion),
    });
    if (ok) {
      this.updates.report({
        stage: 'backing_up',
        percent: 20,
        message: 'Sauvegarde enregistrée — vos données restent aussi dans le dossier utilisateur',
      });
      localStorage.setItem(PRE_UPDATE_BACKUP_KEY, new Date().toISOString());
      await window.electron?.setUpdateState?.({ status: 'backup_confirmed', targetVersion });
      logger.info({ event: 'pre_update_backup_confirmed', preserve_userdata: true });
    }
    return ok;
  }

  /** Confirmation explicite : la MAJ ne touche pas au dossier données. */
  private async confirmDataPreservation(targetVersion: string): Promise<boolean> {
    const platform = detectReleasePlatform();
    return confirmAction({
      title: 'Conserver vos données',
      message: updatePreserveConfirmMessage(this.currentVersion, targetVersion, platform),
      confirmLabel: 'Continuer (données conservées)',
      cancelLabel: 'Annuler',
    });
  }

  async applyOnline(manifest: UpdateManifest): Promise<boolean> {
    // Toujours valider la plateforme du manifeste choisi (CDN ou API), pas une
    // éventuelle ancienne release renvoyée par /v1/releases/latest.
    if (!this.assertPlatformMatch(manifest.platform)) return false;
    if (!this.assertStrictlyNewer(manifest.latest_version)) return false;
    if (!(await this.assertSafeToApply(manifest))) return false;
    if (!this.assertChecksumBeforeBackup(manifest)) return false;
    if (!(await this.confirmDataPreservation(manifest.latest_version))) {
      ToastService.info('Mise à jour annulée — aucune donnée n’a été modifiée');
      return false;
    }
    if (!(await this.mandatoryBackup(manifest.latest_version))) {
      ToastService.error('Sauvegarde préalable requise — mise à jour annulée (données intactes)');
      return false;
    }
    // Ferme proprement la base avant l’installateur (évite fichiers verrouillés).
    await window.electron?.prepareUpdate?.(manifest.latest_version);
    return this.updates.applyUpdate(
      manifest,
      this.ctx.tenantContext.tryGetTenant()?.id ?? 'default',
      { skipBackup: true },
    );
  }

  async applyUsbManifestFile(file: File): Promise<boolean> {
    if (!file.name.toLowerCase().endsWith('.json')) {
      ToastService.error('Sélectionnez un manifeste .json signé ou utilisez « Installateur USB »');
      return false;
    }
    const manifest = await this.usb.loadSignedManifestFromFile(file);
    if (!manifest) {
      ToastService.error('Manifeste USB invalide ou non signé');
      return false;
    }
    if (!window.electron?.verifyUpdateManifest || !window.electron.downloadUpdate) {
      ToastService.error('Disponible uniquement dans l’application desktop');
      return false;
    }
    // La signature est vérifiée par le processus principal : le renderer ne décide de rien.
    const verified = await window.electron.verifyUpdateManifest(manifest);
    if (!verified.ok) {
      ToastService.error(UpdateCoordinator.manifestRefusalMessage(verified.error));
      return false;
    }
    return this.applyVerifiedUsb(
      {
        version: verified.version ?? '',
        mandatory: Boolean(verified.mandatory),
        sha256: verified.sha256 ?? '',
      },
      { signedManifest: manifest },
    );
  }

  /** Message utilisateur pour un code de refus du processus principal. */
  static manifestRefusalMessage(code: string | undefined): string {
    switch (code) {
      case 'manifest_unsigned':
        return 'Manifeste non signé — mise à jour refusée';
      case 'manifest_signature_invalid':
        return 'Signature du manifeste invalide — mise à jour refusée';
      case 'version_not_newer':
        return 'Cette version n’est pas plus récente que celle installée — mise à jour refusée';
      case 'sha256_missing':
        return 'Empreinte SHA-256 absente du manifeste signé — mise à jour refusée';
      default:
        return 'Manifeste de mise à jour invalide — mise à jour refusée';
    }
  }

  /** Mise à jour USB via dialogue système (installateur ou manifeste). */
  async applyUsbViaDialog(): Promise<boolean> {
    if (!window.electron?.pickInstaller) {
      ToastService.error('Disponible uniquement dans l’application desktop');
      return false;
    }
    const picked = await window.electron.pickInstaller();
    if (picked.canceled || !picked.path) return false;
    return this.applyUsbFromPath(picked.path);
  }

  async applyUsbFromPath(filePath: string): Promise<boolean> {
    if (!this.assertAdminSession()) return false;

    if (
      filePath.toLowerCase().endsWith('.json') ||
      filePath.toLowerCase().endsWith('.nexxus-update')
    ) {
      if (!window.electron?.readUpdateManifest) {
        ToastService.error('Lecture manifeste indisponible');
        return false;
      }
      const raw = await window.electron.readUpdateManifest(filePath);
      if (!raw.ok) {
        ToastService.error('Impossible de lire le manifeste');
        return false;
      }
      // Vérification faite par le processus principal (clé publique embarquée).
      if (!raw.verification?.ok) {
        ToastService.error(UpdateCoordinator.manifestRefusalMessage(raw.verification?.error));
        return false;
      }
      if (!raw.installerPath) {
        ToastService.warning(
          'Placez l’installateur à côté du manifeste (local_installer_path signé)',
        );
        return false;
      }
      return this.applyVerifiedUsb(
        {
          version: raw.verification.version ?? '',
          mandatory: Boolean(raw.verification.mandatory),
          sha256: raw.verification.sha256 ?? '',
        },
        { manifestPath: filePath },
      );
    }

    ToastService.error(
      'Sélectionnez le manifeste signé .nexxus-update fourni avec l’installateur.',
    );
    return false;
  }

  /**
   * Mise à jour USB dont le manifeste a DÉJÀ été vérifié par le processus principal.
   * Le lancement se fait par chemin du manifeste (ou manifeste signé complet) : SHA-256 et version
   * sont relus et revérifiés côté principal.
   */
  private async applyVerifiedUsb(
    verified: { version: string; mandatory: boolean; sha256: string },
    source: { manifestPath?: string; signedManifest?: unknown },
  ): Promise<boolean> {
    if (!this.assertStrictlyNewer(verified.version)) return false;
    const platform = detectReleasePlatform();
    const updateManifest: UpdateManifest = {
      latest_version: verified.version,
      current_version: this.currentVersion,
      is_outdated: compareAppVersions(this.currentVersion, verified.version) < 0,
      is_critical: verified.mandatory,
      download_url: '',
      platform,
      architecture: platform === 'mac' ? 'universal' : 'x64',
      signature_url: '',
      checksum_sha256: verified.sha256,
      manifest_signature: '',
      published_at: new Date().toISOString(),
      install_mode: 'signed',
      signed_manifest: source.signedManifest ?? {},
    };

    if (!(await this.assertSafeToApply(updateManifest))) return false;
    if (!this.assertChecksumBeforeBackup(updateManifest)) return false;
    if (!(await this.confirmDataPreservation(verified.version))) {
      ToastService.info('Mise à jour annulée — aucune donnée n’a été modifiée');
      return false;
    }
    if (!(await this.mandatoryBackup(verified.version))) return false;
    await window.electron?.prepareUpdate?.(verified.version);

    if (source.manifestPath && window.electron?.launchLocalInstaller) {
      this.updates.report({
        stage: 'applying',
        percent: 90,
        message: 'Lancement de l’installateur…',
      });
      const launched = await window.electron.launchLocalInstaller(source.manifestPath);
      if (launched.ok) {
        this.updates.report({
          stage: 'restarting',
          percent: 100,
          message: 'Nexxus ERP va redémarrer après l’installation.',
        });
      }
      return launched.ok;
    }

    if (source.signedManifest && window.electron?.downloadUpdate) {
      this.updates.report({
        stage: 'downloading',
        percent: 0,
        message: 'Téléchargement de la mise à jour…',
      });
      const stopProgress = window.electron.onDownloadProgress?.((payload) => {
        this.updates.report({
          stage: 'downloading',
          percent: Number(payload.percent) || 0,
          receivedBytes: payload.received,
          totalBytes: payload.total,
          message: 'Téléchargement de la mise à jour…',
        });
      });
      try {
        await window.electron.downloadUpdate(source.signedManifest);
      } finally {
        stopProgress?.();
      }
      this.updates.report({
        stage: 'restarting',
        percent: 100,
        message: 'Nexxus ERP va redémarrer après l’installation.',
      });
      return true;
    }

    ToastService.warning(
      'Placez l’installateur à côté du manifeste signé ou utilisez un manifeste avec download_url HTTPS',
    );
    return false;
  }

  static formatStatus(release: LicensingLatestRelease | null, current: string): string {
    if (!release) return 'Impossible de contacter le serveur de versions';
    const platform = detectReleasePlatform();
    if (release.platform !== platform) {
      return `Release ${release.version} disponible pour ${release.platform} (poste : ${platform})`;
    }
    if (release.version === current) return `Version ${current} — à jour`;
    return `Version ${release.version} disponible (actuelle : ${current})`;
  }
}
